import type { ManifestFragment } from "@/app/integration/contracts";
import { getB4MathPlayerAssetManifest } from "@/assets/requirements/math/b4-math-player-assets";
import {
  FOREST_MARKET_LEVELS,
  NUMBER_TRAIN_LEVELS,
  PATTERN_BRIDGE_LEVELS,
  type ForestMarketLevel,
  type NumberTrainLevel,
  type PatternBridgeLevel
} from "@/content/math";

import {
  FOREST_MARKET_PLAYER_ADAPTER,
  NUMBER_TRAIN_PLAYER_ADAPTER,
  PATTERN_BRIDGE_PLAYER_ADAPTER
} from "./math-player-adapters";

interface MathPlayerBindingBase {
  readonly assets: ManifestFragment;
  readonly domain: "math";
  readonly levelId: string;
}

export interface NumberTrainPlayerBinding
  extends MathPlayerBindingBase {
  readonly activityType: "number-train";
  readonly adapter: typeof NUMBER_TRAIN_PLAYER_ADAPTER;
  readonly level: NumberTrainLevel;
}

export interface ForestMarketPlayerBinding
  extends MathPlayerBindingBase {
  readonly activityType: "forest-market";
  readonly adapter: typeof FOREST_MARKET_PLAYER_ADAPTER;
  readonly level: ForestMarketLevel;
}

export interface PatternBridgePlayerBinding
  extends MathPlayerBindingBase {
  readonly activityType: "pattern-bridge";
  readonly adapter: typeof PATTERN_BRIDGE_PLAYER_ADAPTER;
  readonly level: PatternBridgeLevel;
}

export type MathPlayerBinding =
  | NumberTrainPlayerBinding
  | ForestMarketPlayerBinding
  | PatternBridgePlayerBinding;

export const NUMBER_TRAIN_PLAYER_BINDINGS: readonly NumberTrainPlayerBinding[] =
  NUMBER_TRAIN_LEVELS.map((level) => ({
    activityType: "number-train",
    adapter: NUMBER_TRAIN_PLAYER_ADAPTER,
    assets: getB4MathPlayerAssetManifest(level.id),
    domain: "math",
    level,
    levelId: level.id
  }));

export const FOREST_MARKET_PLAYER_BINDINGS: readonly ForestMarketPlayerBinding[] =
  FOREST_MARKET_LEVELS.map((level) => ({
    activityType: "forest-market",
    adapter: FOREST_MARKET_PLAYER_ADAPTER,
    assets: getB4MathPlayerAssetManifest(level.id),
    domain: "math",
    level,
    levelId: level.id
  }));

export const PATTERN_BRIDGE_PLAYER_BINDINGS: readonly PatternBridgePlayerBinding[] =
  PATTERN_BRIDGE_LEVELS.map((level) => ({
    activityType: "pattern-bridge",
    adapter: PATTERN_BRIDGE_PLAYER_ADAPTER,
    assets: getB4MathPlayerAssetManifest(level.id),
    domain: "math",
    level,
    levelId: level.id
  }));

export const MATH_PLAYER_BINDINGS: readonly MathPlayerBinding[] = [
  ...NUMBER_TRAIN_PLAYER_BINDINGS,
  ...FOREST_MARKET_PLAYER_BINDINGS,
  ...PATTERN_BRIDGE_PLAYER_BINDINGS
];

export function getMathPlayerBinding(
  levelId: string
): MathPlayerBinding | undefined {
  return MATH_PLAYER_BINDINGS.find(
    (binding) => binding.levelId === levelId
  );
}
